import * as React from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@shared/components/ui/card";
import { Separator } from "@shared/components/ui/separator";
import { SelectProduct } from "@features/products/components/SelectProduct";
import { MultiSelectProduct } from "@features/products/components/MultiSelectProduct";

export function ProductSelectDemo() {
  const [singleProduct, setSingleProduct] = React.useState(null);
  const [multiProducts, setMultiProducts] = React.useState([]);

  return (
    <div className="container mx-auto p-6 space-y-8">
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-bold tracking-tight">
          Product Selection Components Demo
        </h1>
        <p className="text-muted-foreground">
          Demo of the updated SelectProduct component with react-select/async and multi-select functionality
        </p>
      </div>

      <div className="grid gap-8 md:grid-cols-1 lg:grid-cols-2">
        {/* Single Product Select */}
        <Card>
          <CardHeader>
            <CardTitle>Single Product Select</CardTitle>
            <CardDescription>
              Select a single product using react-select/async with shadcn UI styling
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <SelectProduct
              product={singleProduct}
              onSelectProduct={setSingleProduct}
              isMulti={false}
            />
            
            {singleProduct && (
              <div className="p-3 bg-muted rounded-md">
                <h5 className="font-medium text-sm mb-2">Selected Product:</h5>
                <pre className="text-xs overflow-auto">
                  {JSON.stringify(
                    {
                      id: singleProduct._id,
                      name: singleProduct.name,
                      stock: singleProduct.stock,
                      price: singleProduct.price,
                    },
                    null,
                    2
                  )}
                </pre>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Multi Product Select */}
        <Card>
          <CardHeader>
            <CardTitle>Multi Product Select</CardTitle>
            <CardDescription>
              Select multiple products using react-select/async with multi-select enabled
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <SelectProduct
              products={multiProducts}
              onSelectProduct={setMultiProducts}
              isMulti={true}
            />
            
            {multiProducts.length > 0 && (
              <div className="p-3 bg-muted rounded-md">
                <h5 className="font-medium text-sm mb-2">
                  Selected Products ({multiProducts.length}):
                </h5>
                <pre className="text-xs overflow-auto max-h-32">
                  {JSON.stringify(
                    multiProducts.map((product) => ({
                      id: product._id,
                      name: product.name,
                      stock: product.stock,
                      price: product.price,
                    })),
                    null,
                    2
                  )}
                </pre>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Dedicated Multi-Select Component */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Dedicated MultiSelectProduct Component</CardTitle>
            <CardDescription>
              Alternative multi-select component with enhanced UI and individual product cards
            </CardDescription>
          </CardHeader>
          <CardContent>
            <MultiSelectProduct
              products={multiProducts}
              onSelect={setMultiProducts}
              placeholder="Search and select multiple products..."
            />
          </CardContent>
        </Card>
      </div>

      <Separator />

      <div className="text-center space-y-2">
        <h2 className="text-xl font-semibold">Usage Examples</h2>
        <div className="grid gap-4 md:grid-cols-2 text-left">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Single Select Usage</CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="text-xs bg-muted p-3 rounded overflow-auto">
{`<SelectProduct
  product={selectedProduct}
  onSelectProduct={setSelectedProduct}
  isMulti={false}
/>`}
              </pre>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Multi Select Usage</CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="text-xs bg-muted p-3 rounded overflow-auto">
{`<SelectProduct
  products={selectedProducts}
  onSelectProduct={setSelectedProducts}
  isMulti={true}
/>

// Or use dedicated component
<MultiSelectProduct
  products={selectedProducts}
  onSelect={setSelectedProducts}
/>`}
              </pre>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
